import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/shared/auth/auth-store';

/**
 * In dev, Vite proxies `/v1/*` to the API process (see vite.config.ts).
 * In prod, nginx proxies the same path (see docs/10-deployment.md).
 * Either way the browser only talks to its own origin — no CORS, no hard-coded host.
 *
 * Set VITE_API_BASE_URL only if you need to point a built admin bundle at a
 * different host than the one serving it (rare; used by some CI/preview flows).
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export const api = axios.create({
  baseURL: `${BASE_URL}/v1`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let pendingRequests: Array<(token: string) => void> = [];

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          pendingRequests.push((token) => {
            original.headers = original.headers ?? {};
            (original.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
            resolve(api.request(original));
          });
        });
      }

      isRefreshing = true;
      try {
        const refreshRes = await axios.post(
          `${BASE_URL}/v1/auth/refresh`,
          {},
          { withCredentials: true },
        );
        const token = refreshRes.data.data.accessToken as string;
        useAuthStore.getState().setAccessToken(token);
        pendingRequests.forEach((cb) => cb(token));
        pendingRequests = [];
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
        return api.request(original);
      } catch (refreshErr) {
        useAuthStore.getState().clear();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        throw refreshErr;
      } finally {
        isRefreshing = false;
      }
    }

    throw error;
  },
);

export type ApiError = {
  code: string;
  message: string;
  details?: Array<{ field?: string; code: string; message: string }>;
};

export function asApiError(error: unknown): ApiError {
  const e = error as AxiosError<{ error?: ApiError }>;
  return (
    e.response?.data?.error ?? {
      code: 'NETWORK_ERROR',
      message: e.message ?? 'Network error',
    }
  );
}
